import { OrderHistoryInstanceType } from '@flipster/apis/v2/history/instance';
import { ProductType } from '@flipster/apis/v2/history/types';
import { TransOrderType } from '@flipster/apis/v2/order/trans';
import { FormattedMessage } from '@flipster/i18n';
import { Datetime } from '@flipster/react/design-utils/datetime';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { CardListItem as CardItem } from '@flipster/ui/CardList';

import { SymbolDisplay } from '../../../misc/display';
import { useRawSnapshotSpec } from '../../../shared/snapshot/use-snapshot-spec';
import { ExecFeeDisplay } from '../exec-fee-display';
import { HistoryOrderSideBadge } from '../order-side-badge';
import { card, horizontal, symbolRow } from '../styles';

interface Props {
  item: OrderHistoryInstanceType;
  productType: ProductType;
}

export const OrderMobileCard = ({ item, productType }: Props) => {
  const { instrumentsKeyBySymbol } = useRawSnapshotSpec();

  const instrument = instrumentsKeyBySymbol?.[item.symbol];

  return (
    <CardItem>
      <dl className={card}>
        <div className={symbolRow}>
          <SymbolDisplay
            symbol={item.symbol}
            icon={<SymbolDisplay.Icon size={24} />}
            mainLabel={<SymbolDisplay.Label value='shortName' css={{ textStyle: 'Text/L/Strong' }} />}
          />
          <HistoryOrderSideBadge orderSide={item.side} productType={productType} />
        </div>
        <div className={horizontal}>
          <dt>
            <FormattedMessage id='Autogen_common.r251126.s_Type' />
          </dt>
          <dd>{TransOrderType[item.orderType]}</dd>
        </div>
        <div className={horizontal}>
          <dt>Order price</dt>
          <dd>
            {item.isMarket ? (
              <FormattedMessage id='Trade.r251008.Market' />
            ) : (
              <Formatted.Number
                value={item.price}
                zeroString='-'
                precision={instrument?.pricePrecision}
                toFixed
                css={{ textStyle: 'Text/M/Monospace/Regular' }}
                suffix={instrument?.quoteCurrency}
              />
            )}
          </dd>
        </div>
        <div className={horizontal}>
          <dt>
            <FormattedMessage id='Autogen_common.r251126.s_Fill_Order_qty' />
          </dt>
          <dd>
            <Formatted.Number
              value={item.orderQty}
              zeroString='-'
              precision={instrument?.tradeQtyPrecision}
              toFixed
              css={{ textStyle: 'Text/M/Monospace/Regular' }}
            />
          </dd>
        </div>
        <div className={horizontal}>
          <dt>
            <FormattedMessage id='Autogen_common.r251126.s_Fill_price' />
          </dt>
          <dd>
            <Formatted.Number
              value={item.avgPrice}
              zeroString='-'
              precision={instrument?.pricePrecision}
              toFixed
              css={{ textStyle: 'Text/M/Monospace/Regular' }}
              suffix={instrument?.quoteCurrency}
            />
          </dd>
        </div>
        <div className={horizontal}>
          <dt>
            <FormattedMessage id='Autogen_common.r251126.s_Fill_qty' />
          </dt>
          <dd>
            <Formatted.Number
              value={item.cumQty}
              zeroString='-'
              precision={instrument?.tradeQtyPrecision}
              toFixed
              css={{ textStyle: 'Text/M/Monospace/Regular' }}
            />
          </dd>
        </div>
        <div className={horizontal}>
          <dt>
            <FormattedMessage id='Common.r251204.Fee' />
          </dt>
          <dd>
            <ExecFeeDisplay
              originalExecFee={item.cumFee}
              aqxpBonusRedemption={item.cumFeeAqxpRedeemed}
              voucherBonusRedemption={item.cumFeeVoucherRedeemed}
              suffix={instrument?.quoteCurrency}
            />
          </dd>
        </div>
        <div className={horizontal}>
          <dt>
            <FormattedMessage id='Autogen_common.r251126.s_Value' />
          </dt>
          <dd>
            <Formatted.Number
              value={item.fillValue}
              precision={2}
              zeroString='-'
              css={{ textStyle: 'Text/M/Monospace/Regular' }}
              suffix={instrument?.quoteCurrency}
            />
          </dd>
        </div>
        <div className={horizontal}>
          <dt>
            <FormattedMessage id='Autogen_common.r251126.s_Status' />
          </dt>
          <dd className={css({ textAlign: 'right' })}>
            <p>{item.transOrderStatus}</p>
            {!item.isOpenOrder && <Datetime value={item.transactTime} css={{ color: 'legacy.gray.8' }} />}
          </dd>
        </div>
        <div className={horizontal}>
          <dt>
            <FormattedMessage id='Common.r250401.Date' />
          </dt>
          <dd>
            <Datetime value={item.orderTime} />
          </dd>
        </div>
      </dl>
    </CardItem>
  );
};
